import { Icon } from '@iconify/react';
import {
  Checkbox,
  cn,
  Selection,
  SortDescriptor,
  Spinner,
} from '@nextui-org/react';
import { useVirtualizer } from '@tanstack/react-virtual';
import { forwardRef, memo, ReactNode, useCallback, useRef } from 'react';
import { Column, VirtualizationConfig } from './types';

// Sticky header: 16px top gap + 40px header cell (matches NextUI's table header).
const HEADER_HEIGHT = 56;
const SELECTION_COLUMN_WIDTH = 44;
const UNSIZED_COLUMN_MIN_WIDTH = 120;
const DEFAULT_ROW_HEIGHT = 44;
const DEFAULT_OVERSCAN = 8;

type RowItem = { id: string | number };

interface VirtualizedTableProps<T extends RowItem> {
  columns: Column[];
  items: T[];
  virtualization: VirtualizationConfig;
  selectionMode: 'none' | 'single' | 'multiple';
  selectedKeys: Selection;
  onSelectionChange: (keys: Selection) => void;
  sortDescriptor?: SortDescriptor;
  onSortChange: (descriptor: SortDescriptor) => void;
  isLoading?: boolean;
  topContent?: ReactNode;
  tdClassName?: string;
  ariaLabel?: string;
}

/** "220" -> 220px, "30%" stays as is. */
function toCssWidth(width?: string): string | undefined {
  if (!width) return undefined;
  return /^\d+$/.test(width) ? `${width}px` : width;
}

function getMinTableWidth(columns: Column[], hasSelection: boolean): number {
  const columnsWidth = columns.reduce((total, column) => {
    const px =
      column.width && /^\d+$/.test(column.width) ? Number(column.width) : 0;
    return total + (px || UNSIZED_COLUMN_MIN_WIDTH);
  }, 0);

  return columnsWidth + (hasSelection ? SELECTION_COLUMN_WIDTH : 0);
}

function isKeySelected(selectedKeys: Selection, id: string | number) {
  return selectedKeys === 'all' || selectedKeys.has(String(id));
}

interface VirtualRowProps {
  item: RowItem;
  index: number;
  columns: Column[];
  hasSelection: boolean;
  isSelected: boolean;
  onToggle: (id: string | number) => void;
  tdClassName?: string;
}

/**
 * One body row. Memoised so scrolling or selecting another row does not
 * re-render the cells of rows whose data and selection did not change.
 */
const VirtualRow = memo(
  forwardRef<HTMLTableRowElement, VirtualRowProps>(function VirtualRow(
    { item, index, columns, hasSelection, isSelected, onToggle, tdClassName },
    ref
  ) {
    return (
      <tr
        ref={ref}
        data-index={index}
        data-selected={isSelected || undefined}
        aria-selected={hasSelection ? isSelected : undefined}
        className="group outline-none"
      >
        {hasSelection && (
          <td className="px-3 py-1 align-middle first:rounded-l-lg group-hover:bg-default-100 group-data-[selected=true]:bg-default-100">
            <Checkbox
              size="sm"
              aria-label="Select row"
              isSelected={isSelected}
              onValueChange={() => onToggle(item.id)}
            />
          </td>
        )}
        {columns.map((column) => (
          <td
            key={column.uid}
            className={cn(
              'px-3 py-1 align-middle text-small first:rounded-l-lg last:rounded-r-lg group-hover:bg-default-100 group-data-[selected=true]:bg-default-100',
              column.wrap
                ? 'whitespace-normal break-words'
                : 'overflow-hidden text-ellipsis whitespace-nowrap',
              column.align === 'center' && 'text-center',
              column.align === 'end' && 'text-right',
              tdClassName
            )}
          >
            {column.renderCell
              ? column.renderCell(item)
              : String(item[column.uid as keyof RowItem] ?? '')}
          </td>
        ))}
      </tr>
    );
  })
);

/**
 * NextUI-styled table that renders only the rows in view, using
 * @tanstack/react-virtual. Rows are measured after render, so cells that wrap
 * (e.g. several cohorts) keep their natural height.
 */
function VirtualizedTable<T extends RowItem>({
  columns,
  items,
  virtualization,
  selectionMode,
  selectedKeys,
  onSelectionChange,
  sortDescriptor,
  onSortChange,
  isLoading,
  topContent,
  tdClassName,
  ariaLabel,
}: VirtualizedTableProps<T>) {
  const scrollRef = useRef<HTMLDivElement>(null);
  const hasSelection = selectionMode !== 'none';
  const columnCount = columns.length + (hasSelection ? 1 : 0);

  const virtualizer = useVirtualizer({
    count: items.length,
    getScrollElement: () => scrollRef.current,
    estimateSize: () => virtualization.estimatedRowHeight ?? DEFAULT_ROW_HEIGHT,
    overscan: virtualization.overscan ?? DEFAULT_OVERSCAN,
    paddingStart: HEADER_HEIGHT,
    getItemKey: (index) => String(items[index].id),
  });

  const virtualRows = virtualizer.getVirtualItems();
  const paddingTop = virtualRows.length
    ? virtualRows[0].start - HEADER_HEIGHT
    : 0;
  const paddingBottom = virtualRows.length
    ? virtualizer.getTotalSize() - virtualRows[virtualRows.length - 1].end
    : 0;

  // Keep row callbacks stable (memoised rows) while reading fresh selection.
  const selectedKeysRef = useRef(selectedKeys);
  selectedKeysRef.current = selectedKeys;
  const itemsRef = useRef(items);
  itemsRef.current = items;

  const handleToggleRow = useCallback(
    (id: string | number) => {
      const key = String(id);
      const current = selectedKeysRef.current;
      const next = new Set(
        current === 'all'
          ? itemsRef.current.map((item) => String(item.id))
          : Array.from(current, String)
      );

      if (selectionMode === 'single') {
        onSelectionChange(next.has(key) ? new Set() : new Set([key]));
        return;
      }

      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      onSelectionChange(next);
    },
    [onSelectionChange, selectionMode]
  );

  const selectedVisibleCount =
    selectedKeys === 'all'
      ? items.length
      : items.filter((item) => selectedKeys.has(String(item.id))).length;
  const allSelected = items.length > 0 && selectedVisibleCount === items.length;

  const handleToggleAll = (checked: boolean) => {
    onSelectionChange(
      checked ? new Set(items.map((item) => String(item.id))) : new Set()
    );
  };

  const handleSort = (column: Column) => {
    if (!column.sortable) return;
    const isSameColumn = sortDescriptor?.column === column.uid;
    onSortChange({
      column: column.uid,
      direction:
        isSameColumn && sortDescriptor?.direction === 'ascending'
          ? 'descending'
          : 'ascending',
    });
  };

  const headerCellClassName = (isFirst: boolean, isLast: boolean) =>
    cn(
      'flex h-10 items-center gap-1 bg-default-100 px-3 text-tiny font-semibold text-default-500',
      isFirst && 'rounded-l-lg',
      isLast && 'rounded-r-lg'
    );

  return (
    <div className="flex w-full flex-col gap-4">
      {topContent}
      <div
        ref={scrollRef}
        className="relative w-full overflow-auto rounded-large bg-content1 px-4 pb-4 shadow-small"
        style={{ maxHeight: virtualization.maxHeight ?? '70vh' }}
      >
        <table
          aria-label={ariaLabel ?? 'Data Table'}
          aria-rowcount={items.length}
          aria-multiselectable={selectionMode === 'multiple' || undefined}
          className="w-full table-fixed border-separate border-spacing-0"
          style={{ minWidth: getMinTableWidth(columns, hasSelection) }}
        >
          <colgroup>
            {hasSelection && <col style={{ width: SELECTION_COLUMN_WIDTH }} />}
            {columns.map((column) => (
              <col
                key={column.uid}
                style={{ width: toCssWidth(column.width) }}
              />
            ))}
          </colgroup>
          <thead>
            <tr>
              {hasSelection && (
                <th className="sticky top-0 z-20 bg-content1 p-0 pt-4 font-normal">
                  <div
                    className={headerCellClassName(true, columns.length === 0)}
                  >
                    {selectionMode === 'multiple' && (
                      <Checkbox
                        size="sm"
                        aria-label="Select all"
                        isSelected={allSelected}
                        isIndeterminate={
                          selectedVisibleCount > 0 && !allSelected
                        }
                        onValueChange={handleToggleAll}
                      />
                    )}
                  </div>
                </th>
              )}
              {columns.map((column, index) => {
                const isSorted = sortDescriptor?.column === column.uid;
                return (
                  <th
                    key={column.uid}
                    scope="col"
                    aria-sort={
                      isSorted
                        ? sortDescriptor?.direction === 'descending'
                          ? 'descending'
                          : 'ascending'
                        : undefined
                    }
                    className="sticky top-0 z-20 bg-content1 p-0 pt-4 text-left font-normal"
                  >
                    <div
                      className={cn(
                        headerCellClassName(
                          !hasSelection && index === 0,
                          index === columns.length - 1
                        ),
                        column.align === 'center' && 'justify-center',
                        column.align === 'end' && 'justify-end',
                        column.sortable &&
                          'cursor-pointer select-none hover:text-foreground'
                      )}
                      role={column.sortable ? 'button' : undefined}
                      tabIndex={column.sortable ? 0 : undefined}
                      onClick={() => handleSort(column)}
                      onKeyDown={(event) => {
                        if (event.key === 'Enter' || event.key === ' ') {
                          event.preventDefault();
                          handleSort(column);
                        }
                      }}
                    >
                      <span className="truncate">{column.name}</span>
                      {column.sortable && (
                        <Icon
                          icon="lucide:chevron-up"
                          className={cn(
                            'h-3 w-3 shrink-0 transition-transform',
                            isSorted ? 'opacity-100' : 'opacity-0',
                            isSorted &&
                              sortDescriptor?.direction === 'descending' &&
                              'rotate-180'
                          )}
                        />
                      )}
                    </div>
                  </th>
                );
              })}
            </tr>
          </thead>
          {/*
            Own stacking context: NextUI checkboxes use z-10 internally, and
            without this a scrolled row's checkbox paints over (and blocks
            clicks on) the sticky header's "select all" checkbox.
          */}
          <tbody className="isolate">
            {items.length === 0 ? (
              <tr>
                <td
                  colSpan={columnCount}
                  className="h-24 text-center text-small text-default-400"
                >
                  {isLoading ? '' : 'No data to display'}
                </td>
              </tr>
            ) : (
              <>
                {paddingTop > 0 && (
                  <tr aria-hidden="true">
                    <td
                      colSpan={columnCount}
                      style={{ height: paddingTop, padding: 0 }}
                    />
                  </tr>
                )}
                {virtualRows.map((virtualRow) => {
                  const item = items[virtualRow.index];
                  return (
                    <VirtualRow
                      key={virtualRow.key}
                      ref={virtualizer.measureElement}
                      index={virtualRow.index}
                      item={item}
                      columns={columns}
                      hasSelection={hasSelection}
                      isSelected={isKeySelected(selectedKeys, item.id)}
                      onToggle={handleToggleRow}
                      tdClassName={tdClassName}
                    />
                  );
                })}
                {paddingBottom > 0 && (
                  <tr aria-hidden="true">
                    <td
                      colSpan={columnCount}
                      style={{ height: paddingBottom, padding: 0 }}
                    />
                  </tr>
                )}
              </>
            )}
          </tbody>
        </table>
        {isLoading && (
          <div className="absolute inset-0 flex items-center justify-center bg-content1/50">
            <Spinner />
          </div>
        )}
      </div>
    </div>
  );
}

export default VirtualizedTable;
